import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert
} from '@mui/material';
import { useForm } from 'react-hook-form';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';

const GENDER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' }
];

// Helper function to convert years to days (approximate)
const yearsToDays = (years) => {
  if (!years && years !== 0) return '';
  return Math.round(years * 365.25);
};

export const LisTestItemRefrenceRangeForm = ({ 
  onClose, 
  onAdd, 
  parameters = [], 
  existingReferenceRanges = [] 
}) => {
  console.log('=== REFERENCE RANGE FORM PROPS ===');
  console.log('parameters:', parameters);
  console.log('existingReferenceRanges:', existingReferenceRanges);

  // Auto-select first parameter when the form loads
  const [selectedParameter, setSelectedParameter] = useState('');
  const [editingRange, setEditingRange] = useState(null);
  const [formValues, setFormValues] = useState({
    gender: 'all',
    age_from: '',
    age_to: '',
    age_from_in_days: '',
    age_to_in_days: '',
    range_from: '',
    range_to: '',
    cric_range_from: '',
    cric_range_to: ''
  });

  const { register, handleSubmit, reset, setValue, watch, getValues, formState: { errors } } = useForm({
    defaultValues: formValues
  });

  // Watch all form values to sync with local state
  const watchedValues = watch();

  // Sync local state with form values
  useEffect(() => {
    setFormValues(watchedValues);
  }, [watchedValues]);

  // Auto-calculate days when years change
  useEffect(() => {
    if (formValues.age_from !== '') {
      const calculatedDays = yearsToDays(parseFloat(formValues.age_from));
      // Use setTimeout to ensure MUI label updates properly
      setTimeout(() => {
        setValue('age_from_in_days', calculatedDays, { shouldValidate: true, shouldDirty: true });
      }, 0);
    }
  }, [formValues.age_from, setValue]);

  useEffect(() => {
    if (formValues.age_to !== '') {
      const calculatedDays = yearsToDays(parseFloat(formValues.age_to));
      // Use setTimeout to ensure MUI label updates properly
      setTimeout(() => {
        setValue('age_to_in_days', calculatedDays, { shouldValidate: true, shouldDirty: true });
      }, 0);
    }
  }, [formValues.age_to, setValue]);

  // Handle manual input for days fields
  const handleDaysFromChange = (e) => {
    const value = e.target.value;
    setValue('age_from_in_days', value, { shouldValidate: true, shouldDirty: true });
  };

  const handleDaysToChange = (e) => {
    const value = e.target.value;
    setValue('age_to_in_days', value, { shouldValidate: true, shouldDirty: true });
  };

  // Auto-select first parameter when component mounts
  useEffect(() => {
    if (parameters.length > 0 && !selectedParameter) {
      const firstParamId = parameters[0]._id || parameters[0].tempId;
      console.log('Auto-selecting first parameter:', firstParamId);
      setSelectedParameter(firstParamId);
    }
  }, [parameters, selectedParameter]);

  // Filter ranges for the selected parameter
  const filteredRanges = existingReferenceRanges.filter(range => 
    range.parameterId === selectedParameter
  );

  console.log('Selected parameter:', selectedParameter);
  console.log('Filtered ranges for selected parameter:', filteredRanges);

  const handleAddReferenceRange = (data) => {
    if (!selectedParameter) {
      alert('Please select a parameter first');
      return;
    }

    console.log('Adding/Updating reference range:', data);
    console.log('Editing range:', editingRange);

    const rangeData = {
      ...data,
      parameterId: selectedParameter,
      // Convert empty strings to null for cleaner data
      age_from: data.age_from || null,
      age_to: data.age_to || null,
      age_from_in_days: data.age_from_in_days || null,
      age_to_in_days: data.age_to_in_days || null,
      range_from: data.range_from || null,
      range_to: data.range_to || null,
      cric_range_from: data.cric_range_from || null,
      cric_range_to: data.cric_range_to || null,
      // Preserve the original _id and tempId if editing
      _id: editingRange?._id || undefined,
      tempId: editingRange?.tempId || `range-${Date.now()}`,
      // Add a flag to indicate if this is an update
      _isUpdate: !!editingRange
    };

    console.log('Sending to parent:', rangeData);

    // Send to parent immediately
    onAdd(rangeData);

    // Reset form
    resetForm();
  };

  const handleEdit = (range) => {
    console.log('Editing range:', range);
    setSelectedParameter(range.parameterId);
    setEditingRange(range);
    
    // Prepare form values for editing
    const editFormValues = {
      gender: range.gender || 'all',
      age_from: range.age_from || '',
      age_to: range.age_to || '',
      age_from_in_days: range.age_from_in_days || '',
      age_to_in_days: range.age_to_in_days || '',
      range_from: range.range_from || '',
      range_to: range.range_to || '',
      cric_range_from: range.cric_range_from || '',
      cric_range_to: range.cric_range_to || ''
    };
    
    console.log('Setting form values for editing:', editFormValues);
    
    // Reset form first, then set new values with proper MUI label handling
    reset();
    
    // Use setTimeout to ensure MUI labels update properly
    setTimeout(() => {
      Object.keys(editFormValues).forEach(key => {
        setValue(key, editFormValues[key], { shouldValidate: true, shouldDirty: true });
      });
    }, 0);
  };

  const handleDelete = (range) => {
    console.log('Deleting range:', range);
    if (window.confirm('Are you sure you want to delete this reference range?')) {
      // Send delete action to parent
      const deleteData = {
        ...range,
        _isDeleted: true
      };
      onAdd(deleteData);
    }
  };

  const resetForm = () => {
    setEditingRange(null);
    const defaultValues = {
      gender: 'all',
      age_from: '',
      age_to: '',
      age_from_in_days: '',
      age_to_in_days: '',
      range_from: '',
      range_to: '',
      cric_range_from: '',
      cric_range_to: ''
    };
    
    reset(defaultValues);
    setFormValues(defaultValues);
  };

  const handleCancelEdit = () => {
    resetForm();
  };

  const getParameterName = (parameterId) => {
    const param = parameters.find(p => p._id === parameterId || p.tempId === parameterId);
    return param ? param.name : 'Unknown Parameter';
  };

  const handleParameterChange = (parameterId) => {
    console.log('Parameter changed to:', parameterId);
    setSelectedParameter(parameterId);
    resetForm();
  };

  // Get parameter name for display
  const selectedParameterName = selectedParameter ? getParameterName(selectedParameter) : '';

  return (
    <Dialog open={true} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Typography variant="h5" fontWeight="bold">
          Reference Ranges Management
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Add reference ranges for each parameter
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        <Grid container spacing={3}>
          {/* Parameter Selection */}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Select Parameter</InputLabel>
              <Select
                value={selectedParameter}
                onChange={(e) => handleParameterChange(e.target.value)}
                label="Select Parameter"
              >
                {parameters.map((param) => (
                  <MenuItem key={param._id || param.tempId} value={param._id || param.tempId}>
                    {param.name} {param.unit ? `(${param.unit})` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {selectedParameter && (
              <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
                Managing reference ranges for: <strong>{selectedParameterName}</strong>
              </Typography>
            )}
          </Grid>

          {selectedParameter && (
            <>
              {/* Reference Range Form */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  {editingRange ? 'Edit' : 'Add'} Reference Range for {selectedParameterName}
                </Typography>
                <Box component="form" onSubmit={handleSubmit(handleAddReferenceRange)}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth>
                        <InputLabel>Gender</InputLabel>
                        <Select
                          {...register('gender')}
                          label="Gender"
                          value={formValues.gender}
                        >
                          {GENDER_OPTIONS.map(option => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={6} sm={3} md={2}>
                      <TextField
                        {...register('age_from')}
                        label="Age From (years)"
                        type="number"
                        fullWidth
                        value={formValues.age_from}
                        onChange={(e) => setValue('age_from', e.target.value, { shouldValidate: true, shouldDirty: true })}
                      />
                    </Grid>

                    <Grid item xs={6} sm={3} md={2}>
                      <TextField
                        {...register('age_to')}
                        label="Age To (years)"
                        type="number"
                        fullWidth
                        value={formValues.age_to}
                        onChange={(e) => setValue('age_to', e.target.value, { shouldValidate: true, shouldDirty: true })}
                      />
                    </Grid>

                    <Grid item xs={6} sm={3} md={2}>
                      <TextField
                        {...register('age_from_in_days')}
                        label="Age From (days)"
                        type="number"
                        fullWidth
                        value={formValues.age_from_in_days}
                        onChange={handleDaysFromChange}
                        InputProps={{
                          readOnly: formValues.age_from !== '' && formValues.age_from_in_days === yearsToDays(parseFloat(formValues.age_from || 0)).toString()
                        }}
                        sx={{
                          '& .MuiInputBase-input': {
                            backgroundColor: formValues.age_from !== '' && formValues.age_from_in_days === yearsToDays(parseFloat(formValues.age_from || 0)).toString() ? '#f5f5f5' : 'transparent',
                          }
                        }}
                      />
                    </Grid>

                    <Grid item xs={6} sm={3} md={2}>
                      <TextField
                        {...register('age_to_in_days')}
                        label="Age To (days)"
                        type="number"
                        fullWidth
                        value={formValues.age_to_in_days}
                        onChange={handleDaysToChange}
                        InputProps={{
                          readOnly: formValues.age_to !== '' && formValues.age_to_in_days === yearsToDays(parseFloat(formValues.age_to || 0)).toString()
                        }}
                        sx={{
                          '& .MuiInputBase-input': {
                            backgroundColor: formValues.age_to !== '' && formValues.age_to_in_days === yearsToDays(parseFloat(formValues.age_to || 0)).toString() ? '#f5f5f5' : 'transparent',
                          }
                        }}
                      />
                    </Grid>

                    <Grid item xs={6} sm={4}>
                      <TextField
                        {...register('range_from', { required: 'Range From is required' })}
                        label="Range From *"
                        type="number"
                        fullWidth
                        required
                        error={!!errors.range_from}
                        helperText={errors.range_from?.message}
                        value={formValues.range_from}
                        onChange={(e) => setValue('range_from', e.target.value, { shouldValidate: true, shouldDirty: true })}
                      />
                    </Grid>

                    <Grid item xs={6} sm={4}>
                      <TextField
                        {...register('range_to', { required: 'Range To is required' })}
                        label="Range To *"
                        type="number"
                        fullWidth
                        required
                        error={!!errors.range_to}
                        helperText={errors.range_to?.message}
                        value={formValues.range_to}
                        onChange={(e) => setValue('range_to', e.target.value, { shouldValidate: true, shouldDirty: true })}
                      />
                    </Grid>

                    <Grid item xs={6} sm={4}>
                      <TextField
                        {...register('cric_range_from')}
                        label="Critical Range From"
                        type="number"
                        fullWidth
                        value={formValues.cric_range_from}
                        onChange={(e) => setValue('cric_range_from', e.target.value, { shouldValidate: true, shouldDirty: true })}
                      />
                    </Grid>

                    <Grid item xs={6} sm={4}>
                      <TextField
                        {...register('cric_range_to')}
                        label="Critical Range To"
                        type="number"
                        fullWidth
                        value={formValues.cric_range_to}
                        onChange={(e) => setValue('cric_range_to', e.target.value, { shouldValidate: true, shouldDirty: true })}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                        💡 Age in days is automatically calculated from years. Click on days fields to manually override.
                      </Typography>
                      <Button 
                        type="submit" 
                        variant="contained" 
                        color={editingRange ? "warning" : "primary"}
                        disabled={!formValues.range_from || !formValues.range_to}
                      >
                        {editingRange ? 'Update Range' : 'Add Range'}
                      </Button>
                      {editingRange && (
                        <Button 
                          onClick={handleCancelEdit}
                          style={{ marginLeft: '10px' }}
                        >
                          Cancel Edit
                        </Button>
                      )}
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              {/* Existing Reference Ranges Table */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Existing Reference Ranges for {selectedParameterName}
                </Typography>
                {filteredRanges.length === 0 ? (
                  <Alert severity="info">
                    No reference ranges defined for this parameter.
                  </Alert>
                ) : (
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Gender</TableCell>
                          <TableCell>Age Range (Years)</TableCell>
                          <TableCell>Age Range (Days)</TableCell>
                          <TableCell>Normal Range</TableCell>
                          <TableCell>Critical Range</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredRanges.map((range, index) => (
                          <TableRow key={range._id || range.tempId || index}>
                            <TableCell>
                              {GENDER_OPTIONS.find(g => g.value === range.gender)?.label}
                            </TableCell>
                            <TableCell>
                              {range.age_from || range.age_to 
                                ? `${range.age_from || ''}-${range.age_to || ''}`
                                : 'All ages'
                              }
                            </TableCell>
                            <TableCell>
                              {range.age_from_in_days || range.age_to_in_days 
                                ? `${range.age_from_in_days || ''}-${range.age_to_in_days || ''}`
                                : '-'
                              }
                            </TableCell>
                            <TableCell>
                              {range.range_from} - {range.range_to}
                            </TableCell>
                            <TableCell>
                              {range.cric_range_from && range.cric_range_to 
                                ? `${range.cric_range_from} - ${range.cric_range_to}`
                                : '-'
                              }
                            </TableCell>
                            <TableCell>
                              <IconButton 
                                size="small" 
                                onClick={() => handleEdit(range)}
                                color="primary"
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton 
                                size="small" 
                                onClick={() => handleDelete(range)}
                                color="error"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Grid>
            </>
          )}

          {!selectedParameter && parameters.length > 0 && (
            <Grid item xs={12}>
              <Alert severity="warning">
                Please select a parameter to manage its reference ranges.
              </Alert>
            </Grid>
          )}

          {parameters.length === 0 && (
            <Grid item xs={12}>
              <Alert severity="error">
                No parameters available. Please add parameters first.
              </Alert>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={onClose} 
          variant="contained" 
          color="primary"
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};